import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch } from 'react-hook-form';
import { DateTime, Duration } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { ClassNameMap } from '@mui/styles';
import { TimeSignal, TimeSignalAttribute } from 'graphql/types';
import { MIN_SUPPORTED_DATE } from 'utils/date-time';
import { CalendarDateWithToday } from 'modules/common/components/CalendarDate/CalendarDateWithToday';
import { useTimesheetLabelSuggestionContext } from 'modules/timesheet-labels';
import {
  AddNoteFormChangeHandlers,
  useSuggestionProps
} from 'modules/notes/addNote/hooks';
import { NoteClassificationTag } from 'modules/notes/list/NoteClassificationTag';
import { ExcludedTag } from 'modules/notes/list/ExcludedTag';
import { useFeatureFlag } from 'modules/common/hooks';
import { NoteSourcesTable } from '../NoteSourcesTable';
import { NOTE_FIELDS } from '../../noteSchema';
import TimeInput from './TimeInput';
import { NoteDescription, DescriptionChangeProps } from './NoteDescription';

type StyleProps = {
  classes?: ClassNameMap;
  showAttributes?: boolean;
};
const useStyles = makeStyles((theme) => ({
  noteEditor: {
    backgroundColor: 'transparent',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2.5, 0, 2.5)
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  dateTimeContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(2.5)
  },
  dateTime: {
    display: 'flex'
  },
  noteDetails: {
    padding: theme.spacing(2, 2.5, 1, 2.5)
  },
  mainContainer: {
    display: 'flex',
    paddingLeft: ({ showAttributes }: StyleProps) =>
      showAttributes ? theme.spacing(2) : 0
  },
  classification: {
    paddingTop: theme.spacing(0.5),
    margin: theme.spacing(0, 2.5, 0, 2)
  },
  tagContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end'
  }
}));

const useDateStyles = makeStyles((theme) => ({
  inputField: {
    ...theme.typography.body1,
    color: theme.palette.text.primary
  }
}));

type NoteBodyProps = {
  errorMessage: (type: NOTE_FIELDS) => string | null | undefined;
  formChangeHandlers: AddNoteFormChangeHandlers;
  showStartEndTime: boolean;
  commentLabelIndex?: number;
  attributes?: TimeSignalAttribute[] | undefined;
  timeSignal?: TimeSignal;
  disableDate?: boolean;
  classes?: ClassNameMap;
};

const noteDateLabel = <FormattedMessage id="note.startDate" />;

export const NoteBody: React.FC<NoteBodyProps> = ({
  errorMessage,
  formChangeHandlers,
  showStartEndTime,
  disableDate,
  timeSignal,
  attributes,
  classes: overrideClasses
}: NoteBodyProps) => {
  const isTIWebGroupViewEnabled: boolean = useFeatureFlag(
    'isTIWebGroupViewEnabled',
    false
  );

  const showAttributes = useMemo(() => (timeSignal?.sources?.length ?? 0) > 0, [
    timeSignal
  ]);

  const classes = useStyles({
    classes: overrideClasses,
    showAttributes
  });
  const dateStyles = useDateStyles();
  const { setValue, control } = useFormContext();
  const { labelSuggestions } = useTimesheetLabelSuggestionContext();
  const date = useWatch({ name: NOTE_FIELDS.DATE, control });
  const suggestionProps = useSuggestionProps();

  const {
    hasSuggestionStartTime,
    hasSuggestionEndTime,
    hasSuggestionDuration
  } = suggestionProps;

  const [resetTimeValue, setResetTimeValue] = useState<boolean | undefined>(
    false
  );

  const onDateChange = useCallback(
    (date: DateTime) => {
      setValue(NOTE_FIELDS.DATE, date.toJSDate(), { shouldDirty: true });
    },
    [setValue]
  );
  const onDescriptionBlur = useCallback(() => {
    if (hasSuggestionDuration && labelSuggestions?.labelSuggestions.duration) {
      formChangeHandlers.handleDuration(
        Duration.fromObject({
          seconds: labelSuggestions?.labelSuggestions.duration as number
        }),
        showStartEndTime
      ),
        [];
    }
    if (hasSuggestionEndTime && labelSuggestions?.labelSuggestions.endTime) {
      const suggestEndTime = DateTime.fromISO(
        labelSuggestions?.labelSuggestions.endTime
      );

      formChangeHandlers.handleEndTime(suggestEndTime);
    }
    if (
      hasSuggestionStartTime &&
      labelSuggestions?.labelSuggestions.startTime
    ) {
      const suggestStartTime = DateTime.fromISO(
        labelSuggestions?.labelSuggestions.startTime
      );

      formChangeHandlers.handleStartTime(suggestStartTime);
    }
  }, [
    hasSuggestionDuration,
    labelSuggestions?.labelSuggestions.duration,
    labelSuggestions?.labelSuggestions.endTime,
    labelSuggestions?.labelSuggestions.startTime,
    hasSuggestionEndTime,
    hasSuggestionStartTime,
    formChangeHandlers,
    showStartEndTime
  ]);

  const onDescriptionChange = useCallback(
    ({ fetchSuggestions, description }: DescriptionChangeProps) => {
      setResetTimeValue(false);
      fetchSuggestions({
        attributes,
        description,
        hasSuggestionStartTime,
        hasSuggestionEndTime,
        hasSuggestionDuration
      });
    },
    [
      setResetTimeValue,
      attributes,
      hasSuggestionStartTime,
      hasSuggestionEndTime,
      hasSuggestionDuration
    ]
  );

  const calendarDate = useMemo(() => DateTime.fromJSDate(date as Date), [date]);
  return (
    <div className={classes.mainContainer}>
      {timeSignal?.sources && (
        <NoteSourcesTable sources={timeSignal?.sources} />
      )}
      <div className={classes.container}>
        <div className={classes.noteEditor}>
          <NoteDescription
            onDescriptionBlur={onDescriptionBlur}
            onDescriptionChange={onDescriptionChange}
          />
        </div>
        <div className={classes.dateTimeContainer}>
          <div className={classes.dateTime}>
            <CalendarDateWithToday
              testId={'note'}
              hasEndAdornment
              value={calendarDate}
              inputVariant="outlined"
              handleDateChange={onDateChange}
              minDate={MIN_SUPPORTED_DATE}
              fitContent
              disabled={disableDate}
              label={noteDateLabel}
              classes={dateStyles}
            />
            <TimeInput
              errorMessage={errorMessage}
              formChangeHandlers={formChangeHandlers}
              showStartEndTime={showStartEndTime}
              resetTimeValue={resetTimeValue}
              setResetTimeValue={setResetTimeValue}
              suggestionProps={suggestionProps}
            />
          </div>
          <div className={classes.tagContainer}>
            {isTIWebGroupViewEnabled && timeSignal?.excluded && (
              <ExcludedTag note={timeSignal}></ExcludedTag>
            )}
            {timeSignal && (
              <NoteClassificationTag
                noteClassification={timeSignal.classification}
                className={classes.classification}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
